<template>
	<div class="container__planejamento__cubagem">
		<div class="container__planejamento__cubagem__inputs">
			<Card style="padding: 1rem; height: 100%; overflow: auto; max-height: 90vh">
				<div class="container__planejamento__cubagem__header">
					<i
						class="fas fa-angle-down"
						@click="showInputs = !showInputs"
					/>
				</div>
				<PlanejamentoCubagemForm :estrato-florestal-id="estratoFlorestalId" />
				<div
					class="container__planejamento__cubagem__card__content"
					:class="{ show_content: showInputs }"
				/>
			</Card>
		</div>
		<div class="container__planejamento__cubagem__classes">
			<Card style="padding: 1rem; height: calc(100vh - 2rem); overflow: auto">
				<div class="container__planejamento__cubagem__header">
					<label>{{ $t('classes') }}</label>
					<i
						class="fas fa-angle-down"
						@click="showClasses = !showClasses"
					/>
				</div>
				<div
					class="container__planejamento__cubagem__card__content card_classes"
					:class="{
						show_content: showClasses,
						advanced__options__actived: showOpcoesAvancadas,
					}"
				>
					<ClassesCubagem
						v-for="classeFrequencia in chartOptions.series[0].data"
						:key="classeFrequencia.name"
						v-model.number="classeFrequencia.y"
						:intervalo="classeFrequencia.name"
						:excluir="excluirClasse"
					/>
				</div>
			</Card>
		</div>
		<div class="container__planejamento__cubagem__grafico">
			<Card style="padding: 1rem; height: calc(100vh - 6rem); overflow: auto">
				<div class="container__planejamento__cubagem__header">
					<label class="text-H5">{{ $t('distributionDiameterClasses') }}</label>
					<Checkbox
						v-model="planejamentoCubagem.showAsDAP"
						class="w-fit justify-end"
						label="Mostrar como DAP"
					/>
				</div>
				<div
					class="container__planejamento__cubagem__card__content card_grafico"
					:class="{ show_content: showGrafico }"
				>
					<ClassesCubagem
						v-show="classeEFrequenciaEditando.name"
						v-model.number="classeEFrequenciaEditando.y"
						class="container__planejamento__cubagem__card__content__cardedit"
						:intervalo="classeEFrequenciaEditando.name"
						:excluir="excluirClasse"
					/>
					<Chart
						v-if="daps.length"
						ref="ref__chart"
						:options="chartOptions"
					/>
					<label v-else>
						<Loading v-if="buscandoMedicoes || buscandoPlanejamento" />
						{{ statusAtual }}
					</label>
				</div>
			</Card>
		</div>
	</div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import i18n from '../../../VueI18n'

import { Chart } from 'highcharts-vue'
import Card from '../../../components/Card/Card'
import ClassesCubagem from '../../../components/ClassesCubagem/ClassesCubagem'
import Loading from '../../../components/Loading/Loading'
import Checkbox from '../../../components/ui/checkbox/Checkbox'

import { usePlanejamentoCubagem } from '../../../composables/usePlanejamentoCubagem'

import PlanejamentoCubagemForm from './PlanejamentoCubagemForm.vue'

import { getDropdownMedicaoPorEstratoFlorestal } from '../../../services/requests/MedicoesInventarioRequests'
import { buscarPlanejamentoDeOrdemDeServicoDeCubagem } from '../../../services/requests/OrdemDeServicoRequests'

const {
	daps,
	caps,
	series,
	restaurar,
	planejamentoCubagem,
	classesEFrequencias,
	categories,
	excluirClasse,
	classeEFrequenciaEditando,
	ordemServicoId,
	estratoFlorestalId,
	dadosPlanejamentoCubagem,
} = usePlanejamentoCubagem()

const buscandoMedicoes = ref(false)
const buscandoPlanejamento = ref(false)
const medicoes = ref([])
const statusAtual = computed(() => {
	if (buscandoMedicoes.value) {
		return i18n.t('helpStatusCubingFetchMeasure')
	} else if (medicoes.value.length === 0) {
		return i18n.t('helpNotFoundMeasureCubing')
	} else if (buscandoPlanejamento.value) {
		return i18n.t('helpStatusSOCubing')
	}
	return i18n.t('helpNotFoundCubingPlanning')
})

const showInputs = ref(false)
const showClasses = ref(false)
const showGrafico = ref(false)
const showOpcoesAvancadas = ref(false)

const chartOptions = computed(() => {
	return {
		/*chart: {
      width: 900
    },*/
		/**
		 * Creditos no canto inferior direito do grafico
		 */
		credits: {
			enabled: false,
		},
		exporting: {
			enabled: false,
		},
		/**
		 * Dados que devem ser colocados no grafico
		 */
		series: [
			{
				cursor: 'pointer',
				type: 'column',
				name: 'Total de árvores cubadas',
				data: series.value,
				dataLabels: {
					align: 'right',
					crop: false,
					enabled: true,
					style: {
						fontFamily: 'Open Sans, sans-serif',
						fontSize: '.75rem',
						fontWeight: 400,
					},
					rotation: -90,
					y: -45,
				},
				events: {
					click: ({ point }) => {
						if (point.category === classeEFrequenciaEditando.value.name)
							classeEFrequenciaEditando.value = {}
						else classeEFrequenciaEditando.value = point.options
					},
				},
			},
		],
		/**
		 * Titulo do grafico
		 */
		title: {
			text: '',
		},
		/**
		 * Legendas do gráfico
		 */
		legend: {
			enabled: false,
		},
		/**
		 * Eixo X
		 */
		xAxis: {
			gridLineColor: '#000000',
			type: 'category',
			tickInterval: 1,
			visible: true,
			categories: categories.value,
			title: {
				text: `Intervalo de ${planejamentoCubagem.value.showAsDAP ? 'DAP' : 'CAP'} (cm)`,
				style: {
					fontFamily: 'Open Sans, sans-serif',
					fontSize: '.8rem',
					fontWeight: 600,
				},
			},
			labels: {
				rotation: 270,
				style: {
					color: '#01395e',
					fontFamily: 'Open Sans, sans-serif',
					fontSize: '.8rem',
					fontWeight: 700,
				},
			},
		},
		/**
		 * Eixo Y
		 */
		yAxis: {
			gridLineColor: '#f2f2f2',
			title: {
				text: 'Total de árvores cubadas',
				style: {
					fontFamily: 'Open Sans, sans-serif',
					fontSize: '.8rem',
					fontWeight: 600,
				},
			},
			labels: {
				enabled: true,
			},
		},
	}
})

function buscaPlanejamentoDeOrdemDeServicoDeCubagem(idOrdemDeServico) {
	buscandoPlanejamento.value = true
	buscarPlanejamentoDeOrdemDeServicoDeCubagem(idOrdemDeServico).then((response) => {
		if (!response.data.isError && response.data.result !== null) {
			// Informacoes da cubagem
			const { result: dadosCubagem } = response.data
			if (dadosCubagem !== undefined) {
				dadosPlanejamentoCubagem.value = dadosCubagem

				if (dadosCubagem.numero_classes === 0 || dadosCubagem.total_arvores_cubagem === 0) {
					// Se o servidor nao retornou os valores de numero de classes e total de arvores cubagem, entao
					// vamos calcular esses valores a partir das classes e frequencias que vieram do servidor
					planejamentoCubagem.value.numeroClasses = dadosCubagem.classeEFrequencias.length
					planejamentoCubagem.value.totalArvoresCubagem = dadosCubagem.classeEFrequencias
						.map((m) => m.clfr_nr_frequencia_absoluta_cubagem)
						.reduce((somaParcial, valorAtual) => somaParcial + valorAtual, 0)
				} else {
					planejamentoCubagem.value.numeroClasses = dadosCubagem.numero_classes
					planejamentoCubagem.value.totalArvoresCubagem = dadosCubagem.total_arvores_cubagem
				}
				planejamentoCubagem.value.medicaoId = dadosCubagem.idDaMedicao
				planejamentoCubagem.value.distribuicaoUniforme = dadosCubagem.classeEFrequencias
					.map((m) => m.clfr_nr_frequencia_absoluta_cubagem)
					.every((val, i, arr) => val === arr[0])
				daps.value = dadosCubagem.daps
				caps.value = dadosCubagem.caps
				planejamentoCubagem.value.amostraMinima = Math.min(
					...dadosCubagem.classeEFrequencias.map((m) => m.clfr_md_dap_minimo),
				)
				planejamentoCubagem.value.amostraMaxima = Math.max(
					...dadosCubagem.classeEFrequencias.map((m) => m.clfr_md_dap_maximo),
				)

				// Classes e Frequencias que vieram do servidor
				classesEFrequencias.value = dadosCubagem.classeEFrequencias.map((m) => {
					return {
						amostraMin: m.clfr_md_dap_minimo,
						amostraMax: m.clfr_md_dap_maximo,
						classe: m.clfr_nm_classe,
						freqAbsoluta: m.clfr_nr_frequencia_absoluta_cubagem,
						freqRelativa: m.clfr_nr_frequencia_relativa,
						freqAbsolutaCubagem: m.clfr_nr_frequencia_absoluta_cubagem,
					}
				})
			}
		}

		buscandoPlanejamento.value = false
	})
}

function preencherListaDeMedicoes(idEsfl) {
	getDropdownMedicaoPorEstratoFlorestal(idEsfl).then((response) => {
		medicoes.value = response
		buscaPlanejamentoDeOrdemDeServicoDeCubagem(ordemServicoId.value)
		buscandoMedicoes.value = false
	})
}

onMounted(() => {
	const rgbDefaultColor = getComputedStyle(document.body).getPropertyValue('--color-default')
	chartOptions.value.series[0].color = `rgb(${rgbDefaultColor})`

	// Busca medicoes que estao associados a este estrato florestal
	preencherListaDeMedicoes(estratoFlorestalId.value)
})

watch(
	classeEFrequenciaEditando,
	(classeEFrequenciaNova) => {
		if (classeEFrequenciaNova.name) {
			const indiceParaAlterar = classesEFrequencias.value.findIndex(
				(classeEFrequencia) => classeEFrequencia.classe === classeEFrequenciaNova.name,
			)

			if (indiceParaAlterar !== -1) {
				classesEFrequencias.value[indiceParaAlterar].freqAbsolutaCubagem = classeEFrequenciaNova.y
			}
		}
	},
	{ deep: true },
)

onUnmounted(() => {
	planejamentoCubagem.value.medicaoId = ''
	classesEFrequencias.value = []
	daps.value = []
	caps.value = []
	restaurar()
})
</script>

<style scoped>
.container__planejamento__cubagem {
	background-color: white;
	display: grid;
	grid-template: 'inputs' 'classes' 'grafico';
	grid-gap: 0.5rem;
	position: fixed;
	left: 0;
	top: 4rem;
	height: 100%;
	width: 100vw;
}

.container__planejamento__cubagem__header {
	color: rgb(var(--color-primary));
	font-weight: 600;
	font-size: 1rem;
	cursor: default;
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.container__planejamento__cubagem__header__title {
	display: flex;
	align-items: center;
}

.container__planejamento__cubagem__header__title label {
	margin-right: 0.5rem;
}

.container__planejamento__cubagem__header > i {
	cursor: pointer;
}

.container__planejamento__cubagem__inputs {
	grid-area: inputs;
}

.container__planejamento__cubagem__classes {
	grid-area: classes;
}

.container__planejamento__cubagem__grafico {
	grid-area: grafico;
}

.container__planejamento__cubagem__card__content {
	display: none;
	/* margin-top: 1rem; */
}

.container__planejamento__cubagem__card__content.show_content {
	display: block;
}

.container__planejamento__cubagem__card__content.show_content.card_classes {
	max-height: 10rem;
	overflow-y: auto;
}

.container__planejamento__cubagem__card__content.card_grafico label {
	color: var(--color-gray);
	font-family: 'Open Sans', sans-serif;
	font-size: 0.9rem;
	text-align: center;
	width: 100%;
}

.container_planejamento_cubagem_input_com_tooltip {
	display: flex;
	align-items: flex-end;
}

@media screen and (min-width: 768px) {
	.container__planejamento__cubagem {
		grid-template:
			'inputs classes'
			'grafico grafico';
		grid-template-columns: 1fr 1fr;
	}

	.container__planejamento__cubagem__header > i {
		display: none;
	}

	.container__planejamento__cubagem__card__content {
		display: block;
	}

	.container__planejamento__cubagem__card__content.card_classes {
		max-height: 28rem;
		overflow-y: auto;
	}

	.container__planejamento__cubagem__card__content.card_classes.advanced__options__actived {
		max-height: 53rem;
	}

	.container__planejamento__cubagem__card__content.card_grafico {
		display: grid;
	}

	.container__planejamento__cubagem__card__content__cardedit {
		width: 19rem;
		max-height: 5rem;
		justify-self: center;
		position: absolute;
		top: 10px;
		z-index: 1;
	}
}

@media screen and (min-width: 1200px) {
	.container__planejamento__cubagem {
		grid-template: 'inputs grafico';
		grid-template-columns: 1.2fr 3fr;
	}

	.container__planejamento__cubagem__classes {
		display: none;
	}

	.container__planejamento__cubagem__grafico {
		/* max-height: 45rem; */
	}

	.container__planejamento__cubagem__card__content.card_grafico {
		position: relative;
		height: 100%;
		margin-top: 0;
	}

	.container__planejamento__cubagem__card__content.card_grafico label {
		position: absolute;
		top: 50%;
		transform: translateY(-50%);
	}
}
</style>
